import React, { useState, useEffect, useCallback } from 'react';
import { getServerURL } from '../config/api';
import { FaTrash, FaDownload, FaRedo, FaSearch, FaFilter, FaTimes, FaRobot } from 'react-icons/fa';
import './SavedScans.css';

const SavedScans = () => {
  const [savedScans, setSavedScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [scannerFilter, setScannerFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [analyzingIds, setAnalyzingIds] = useState([]);

  // Handle manual AI analysis trigger
  const handleAnalyzeClick = async (scanId) => {
    if (analyzingIds.includes(scanId)) return;

    try {
      setAnalyzingIds(prev => [...prev, scanId]);
      const serverURL = getServerURL(); // Use config function
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/saved-scans/${scanId}/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // Update local state to reflect new AI data
        setSavedScans(prevScans => prevScans.map(scan => {
          if (scan.id === scanId) {
            // Merge the new analysis into metadata
            let currentMetadata = typeof scan.metadata === 'string'
              ? JSON.parse(scan.metadata || '{}')
              : (scan.metadata || {});

            currentMetadata.aiAnalysis = data.aiAnalysis;
            currentMetadata.description = data.aiAnalysis.description;

            const updatedScan = {
              ...scan,
              metadata: JSON.stringify(currentMetadata), // Keep as string for consistency
              product_name: data.aiAnalysis.title || scan.product_name,
              category: data.aiAnalysis.category || scan.category,
              description: data.aiAnalysis.description || scan.description
            };

            // Also update selectedScan if it's the one currently open
            if (selectedScan && selectedScan.id === scanId) {
              setSelectedScan(updatedScan);
            }

            return updatedScan;
          }
          return scan;
        }));
      } else {
        alert('Analysis failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error triggering AI analysis:', error);
      alert('Error connecting to server for analysis');
    } finally {
      setAnalyzingIds(prev => prev.filter(id => id !== scanId));
    }
  };

  // Fetch saved scans from database
  const fetchSavedScans = useCallback(async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      console.log('Fetching saved scans from API...');
      // Use the saved-scans endpoint to get only manually saved scans
      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/saved-scans`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      console.log('Saved scans API Response:', data);

      if (data.success) {
        const scans = data.savedScans || [];
        console.log('📊 Total saved scans loaded:', scans.length);

        // Debug: Show breakdown of scan types and sources
        const typeBreakdown = {};
        const sourceBreakdown = {};

        scans.forEach(scan => {
          const type = scan.barcode_type || 'unknown';
          const source = scan.source || 'unknown';

          typeBreakdown[type] = (typeBreakdown[type] || 0) + 1;
          sourceBreakdown[source] = (sourceBreakdown[source] || 0) + 1;
        });

        setSavedScans(scans);
      } else {
        console.error('API returned error:', data.error);
        setSavedScans([]);
      }
    } catch (error) {
      console.error('Error fetching saved scans:', error);
      setSavedScans([]);
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
      setIsRefreshing(false);
    }
  }, []);

  // Load saved scans on component mount and set up auto-refresh
  useEffect(() => {
    fetchSavedScans();

    // Auto-refresh every 15 seconds
    const refreshInterval = setInterval(() => {
      fetchSavedScans(true);
    }, 15000);

    return () => clearInterval(refreshInterval);
  }, [fetchSavedScans]);

  // Handle scan row click to show details
  const handleScanClick = (scan) => {
    setSelectedScan(scan);
    setShowScanDialog(true);
  };

  // Close scan details dialog
  const closeScanDialog = () => {
    setSelectedScan(null);
    setShowScanDialog(false);
  };

  // Delete saved scan
  const deleteSavedScan = async (scanId) => {
    if (!window.confirm('Are you sure you want to delete this saved scan?')) {
      return;
    }

    try {
      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/saved-scans/${scanId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (result.success) {
        setSavedScans(prev => prev.filter(scan => scan.id !== scanId));
        alert('Saved scan deleted successfully!');
      } else {
        alert('Failed to delete saved scan: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting saved scan:', error);
      alert('Error deleting saved scan. Please try again.');
    }
  };

  // Get unique scanner IDs
  const uniqueScanners = [...new Set(savedScans.map(b => b.device_id || b.source || 'UNKNOWN'))];

  // Filter and sort saved scans
  const filteredSavedScans = savedScans
    .filter(scan => {
      // Show all ESP32 scans (source = "ESP32")
      const source = (scan.source || '').toUpperCase();
      if (source !== 'ESP32') {
        return false;
      }

      const matchesSearch = searchTerm === '' ||
        scan.barcode_data.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesScanner = scannerFilter === 'all' ||
        (scan.device_id || scan.source || 'UNKNOWN') === scannerFilter;

      return matchesSearch && matchesScanner;
    })
    .sort((a, b) => {
      const dateA = new Date(a.saved_at || a.created_at);
      const dateB = new Date(b.saved_at || b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  // Calculate pagination
  const totalPages = Math.ceil(filteredSavedScans.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSavedScans = filteredSavedScans.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOrder, scannerFilter]);

  // Clear all saved scans
  const clearAllSavedScans = async () => {
    if (!window.confirm('⚠️ Are you sure you want to delete ALL saved scans? This cannot be undone!')) {
      return;
    }

    try {
      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      const response = await fetch(`${serverURL}/api/saved-scans/clear`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSavedScans([]);
        alert('✅ All saved scans cleared successfully!');
      } else {
        const errorData = await response.json();
        alert('❌ Error clearing saved scans: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error clearing saved scans:', error);
      alert('❌ Error clearing saved scans. Please try again.');
    }
  };

  // Export saved scans to CSV
  const exportToCSV = () => {
    const csvContent = [
      ['Barcode Data', 'Source', 'Date', 'Time'],
      ...filteredSavedScans.map(scan => {
        // Extract device_id from metadata if available
        let deviceId = scan.device_id || scan.source || 'UNKNOWN';
        try {
          const metadata = typeof scan.metadata === 'string' ? JSON.parse(scan.metadata) : scan.metadata;
          if (metadata && metadata.device_id) {
            deviceId = metadata.device_id;
          }
        } catch (e) {
          // Ignore parsing errors
        }

        return [
          scan.barcode_data,
          deviceId,
          new Date(scan.saved_at || scan.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }),
          new Date(scan.saved_at || scan.created_at).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
          })
        ];
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saved_scans_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="saved-scans-page">
      <div className="page-header">
        <h1>Saved Scans</h1>
        <p>View and manage all your saved barcode scans</p>
      </div>

      {/* Search and Filter Controls */}
      <div className="controls-section">
        <div className="search-filter-container">
          <div className="search-input">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search saved scans or products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-dropdown">
            <FaFilter className="filter-icon" />
            <select
              value={scannerFilter}
              onChange={(e) => setScannerFilter(e.target.value)}
            >
              <option value="all">All Scanners</option>
              {uniqueScanners.map(scanner => (
                <option key={scanner} value={scanner}>{scanner}</option>
              ))}
            </select>
          </div>

          <div className="filter-dropdown">
            <FaFilter className="filter-icon" />
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="btn btn-secondary"
            onClick={() => fetchSavedScans(false)}
            disabled={loading}
          >
            <FaRedo />
            {loading ? 'Loading...' : 'Refresh'}
          </button>

          <button
            className="btn btn-primary"
            onClick={exportToCSV}
            disabled={filteredSavedScans.length === 0}
          >
            <FaDownload />
            Export CSV
          </button>

          <button
            className="btn btn-danger"
            onClick={clearAllSavedScans}
            disabled={savedScans.length === 0}
          >
            <FaTrash />
            Clear All
          </button>
        </div>
      </div>

      {/* Saved Scans Table */}
      {/* Saved Scans Table */}
      <div className="saved-scans-content">
        <div className="scans-summary">
          <div className="summary-info">
            <h2>Saved Scans History</h2>
            <span className="total-count">
              {loading ? 'Loading...' : `${filteredSavedScans.length} of ${savedScans.length} saved scans`}
              {!loading && totalPages > 1 && (
                <span className="page-info"> • Page {currentPage} of {totalPages}</span>
              )}
            </span>
          </div>
        </div>

        <div className="scans-table-container">
          <table className="scans-table">
            <thead>
              <tr>

                <th>Barcode Data</th>
                <th>Source</th>
                <th>Saved Date</th>
                <th>Saved Time</th>
                <th className="action-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="loading-cell" style={{ textAlign: 'center', padding: '40px', color: '#5F6368' }}>
                    <div className="spinner-small" style={{
                      display: 'inline-block',
                      width: '20px',
                      height: '20px',
                      border: '2px solid #E3821E',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '10px',
                      verticalAlign: 'middle'
                    }}></div>
                    Loading saved scans...
                  </td>
                </tr>
              ) : filteredSavedScans.length > 0 ? (
                currentSavedScans.map((scan, index) => (
                  <tr
                    key={scan.id || index}
                    className="scan-row"
                    onClick={() => handleScanClick(scan)}
                  >

                    <td className="scan-data">
                      <span className="scan-text">
                        {scan.barcode_data}
                      </span>
                    </td>
                    <td className="scan-source">
                      <span className={`source-badge source-robridgeai`}>
                        {(() => {
                          // Try to get device_id from metadata first
                          try {
                            const metadata = typeof scan.metadata === 'string' ? JSON.parse(scan.metadata) : scan.metadata;
                            if (metadata && metadata.device_id) {
                              return metadata.device_id;
                            }
                          } catch (e) {
                            // Ignore parsing errors
                          }
                          // Fall back to direct fields
                          return scan.device_id || scan.source || 'UNKNOWN';
                        })()}
                      </span>
                    </td>
                    <td className="scan-date">
                      {(() => {
                        // Try to get the actual scan timestamp from metadata
                        try {
                          const metadata = typeof scan.metadata === 'string' ? JSON.parse(scan.metadata) : scan.metadata;
                          if (metadata && metadata.timestamp) {
                            // Validate timestamp (ESP32 sends millis() which is invalid)
                            let timestamp = metadata.timestamp;
                            if (typeof timestamp === 'number' && timestamp < 946684800000) {
                              // Invalid timestamp (before year 2000), use saved_at instead
                              timestamp = scan.saved_at || scan.created_at;
                            }
                            return new Date(timestamp).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            });
                          }
                        } catch (e) {
                          console.error('Error parsing metadata timestamp:', e);
                        }
                        // Fallback to saved_at or created_at
                        return new Date(scan.saved_at || scan.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        });
                      })()}
                    </td>
                    <td className="scan-time">
                      {(() => {
                        // Try to get the actual scan timestamp from metadata
                        try {
                          const metadata = typeof scan.metadata === 'string' ? JSON.parse(scan.metadata) : scan.metadata;
                          if (metadata && metadata.timestamp) {
                            // Validate timestamp (ESP32 sends millis() which is invalid)
                            let timestamp = metadata.timestamp;
                            if (typeof timestamp === 'number' && timestamp < 946684800000) {
                              // Invalid timestamp (before year 2000), use saved_at instead
                              timestamp = scan.saved_at || scan.created_at;
                            }
                            return new Date(timestamp).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          }
                        } catch (e) {
                          console.error('Error parsing metadata timestamp:', e);
                        }
                        // Fallback to saved_at or created_at
                        return new Date(scan.saved_at || scan.created_at).toLocaleTimeString('en-GB', {
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      })()}
                    </td>
                    <td className="action-cell">
                      <button
                        className="delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSavedScan(scan.id);
                        }}
                        title="Delete saved scan"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5">
                    <div className="empty-state">
                      <div className="empty-icon">
                        <FaSearch />
                      </div>
                      <h3>No Saved Scans Found</h3>
                      <p>
                        {searchTerm || scannerFilter !== 'all'
                          ? 'No saved scans match your current search or filter criteria.'
                          : 'No scans have been saved yet. Scans are automatically saved when you use the scanner.'
                        }
                      </p>
                      {(searchTerm || scannerFilter !== 'all') && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setSearchTerm('');
                            setScannerFilter('all');
                            setSortOrder('newest');
                          }}
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {!loading && totalPages > 1 && (
            <div className="pagination-container" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderTop: '1px solid #E8EAED'
            }}>
              <div className="pagination-info" style={{ color: '#5F6368', fontSize: '14px' }}>
                Showing {startIndex + 1}-{Math.min(endIndex, filteredSavedScans.length)} of {filteredSavedScans.length} saved scans
              </div>
              <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  Previous
                </button>

                <span style={{ color: '#5F6368', fontSize: '14px' }}>
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scan Details Dialog */}
      {showScanDialog && selectedScan && (
        <div className="scan-dialog-overlay" onClick={closeScanDialog}>
          <div className="scan-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="scan-dialog-header">
              <h2>Saved Scan Details</h2>
              <button className="close-btn" onClick={closeScanDialog}>
                <FaTimes />
              </button>
            </div>

            <div className="scan-dialog-body">
              {/* Basic Information Section */}
              <div className="scan-info-section">
                <h3>Basic Information</h3>
                <div className="info-grid-two-columns">
                  <div className="info-column">
                    <div className="info-field">
                      <label>Source</label>
                      <span className="info-value source-badge">
                        {(() => {
                          try {
                            const metadata = typeof selectedScan.metadata === 'string' ? JSON.parse(selectedScan.metadata) : selectedScan.metadata;
                            return metadata?.deviceName || selectedScan.device_id || selectedScan.source || 'Unknown Device';
                          } catch (e) {
                            return selectedScan.device_id || selectedScan.source || 'Unknown Device';
                          }
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className="info-column">
                    <div className="info-field">
                      <label>Time Info</label>
                      <span className="info-value">
                        {new Date(selectedScan.saved_at || selectedScan.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Barcode Data Section */}
              <div className="barcode-data-section">
                <h3>Barcode Data</h3>
                <div className="barcode-data-container">
                  <span className="barcode-data-text">{selectedScan.barcode_data}</span>
                </div>
              </div>

              {/* AI Analysis / Description Section */}
              <div className="description-section">
                <h3>🤖 AI Analysis</h3>
                <div className="description-container">
                  <div className="description-text">
                    {(() => {
                      try {
                        let description = 'No description available';
                        let hasAI = false;
                        let metadata = null;

                        // Try to get description from metadata
                        if (selectedScan.metadata) {
                          metadata = typeof selectedScan.metadata === 'string'
                            ? JSON.parse(selectedScan.metadata)
                            : selectedScan.metadata;

                          if (metadata.aiAnalysis?.description) {
                            description = metadata.aiAnalysis.description;
                            hasAI = true;
                          } else if (metadata.aiAnalysis?.description_short) {
                            description = metadata.aiAnalysis.description_short;
                            hasAI = true;
                          } else if (metadata.description &&
                            metadata.description !== 'No description available' &&
                            metadata.description !== 'No AI analysis available') {
                            description = metadata.description;
                            hasAI = true;
                          }
                        }

                        // Fallback to description field on the scan object itself if metadata failed
                        if (!hasAI && selectedScan.description &&
                          selectedScan.description !== 'No description available' &&
                          selectedScan.description !== 'No AI analysis available') {
                          description = selectedScan.description;
                          // Assume if it has a description but no AI metadata structure, it MIGHT be basic or old AI. 
                          // But usually description is populated by AI. 
                          hasAI = true;
                        }

                        // Check if it is a "Basic Scan"
                        const isBasicScan = metadata?.aiAnalysis?.noAI === true ||
                          (!metadata?.aiAnalysis && (
                            description.includes('Basic scan without AI analysis') ||
                            description.includes('Basic scan - no AI analysis')
                          ));

                        const renderButton = () => (
                          <div className="ai-analysis-trigger" style={{ marginTop: '0' }}>
                            <p className="description-placeholder">
                              {(hasAI && isBasicScan)
                                ? 'Basic details shown above. Analyse with AI to get more details on the scanned data'
                                : 'No AI analysis performed yet.'}
                            </p>
                            <button
                              className="btn-ai-analyze"
                              onClick={() => handleAnalyzeClick(selectedScan.id)}
                              disabled={analyzingIds.includes(selectedScan.id)}
                            >
                              {analyzingIds.includes(selectedScan.id) ? (
                                <>
                                  <div className="spinner-micro"></div>
                                  Analyzing...
                                </>
                              ) : (
                                <>
                                  <FaRobot /> Analyze with AI
                                </>
                              )}
                            </button>
                          </div>
                        );

                        // 1. If it's a Basic Scan: Show ONLY Button (User requested to remove the text)
                        if (hasAI && isBasicScan) {
                          return renderButton();
                        }

                        // 2. If it's a Real AI Scan: Show Description ONLY
                        if (hasAI) {
                          return description;
                        }

                        // 3. If No Data: Show Button ONLY
                        return renderButton();

                      } catch (error) {
                        console.error('Error parsing metadata:', error);
                        return selectedScan.product_name || 'No description available';
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="scan-dialog-footer">
              <button className="btn btn-danger" onClick={() => {
                deleteSavedScan(selectedScan.id);
                closeScanDialog();
              }}>
                <FaTrash /> Delete Saved Scan
              </button>
              <button className="btn btn-secondary" onClick={closeScanDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Background Refresh Loader */}
      {isRefreshing && (
        <div className="fixed-loader" style={{
          position: 'fixed',
          bottom: '24px',
          left: '300px',
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '8px 16px',
          borderRadius: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 1000,
          border: '1px solid #E8EAED',
          fontSize: '13px',
          color: '#5F6368',
          fontWeight: 500
        }}>
          <div className="spinner-small" style={{
            width: '14px',
            height: '14px',
            border: '2px solid #E3821E',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Updating scans...
        </div>
      )}
    </div>
  );
};

export default SavedScans;

